import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { verifyLogin } from "../../api/api-autenticacion";
import {
  STRING_ACCESS_TOKEN_TUTTIENGLISH,
  STRING_LANGUAGE_TUTTIENGLISH,
  STRING_SIDEBAR_EXPANDED_TUTTIENGLISH,
} from "../../utils/const/string-const";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n/i18n";
import { Eye, EyeOff } from "lucide-react";
import { VERSION_TUTTIENGLISH } from "../../utils/const/version-const";

export default function Login() {
  const { t } = useTranslation();
  const history = useHistory();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const languages = [
    { value: "en", label: "English", flag: "🇺🇸" },
    { value: "es", label: "Español", flag: "🇪🇸" },
    { value: "pt", label: "Português", flag: "🇧🇷" },
  ];

  useEffect(() => {
    const language = localStorage.getItem(STRING_LANGUAGE_TUTTIENGLISH);
    if (language) {
      setSelectedLanguage(language);
      i18n.changeLanguage(language);
    } else {
      i18n.changeLanguage("en");
      setSelectedLanguage("en");
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isLanguageDropdownOpen &&
        !event.target.closest(".language-dropdown")
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLanguageDropdownOpen]);

  const submit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      const response = await verifyLogin(usuario, password);
      console.log('response');
      console.log(response);
      setLoading(false);
      if (response.error === false) {
        localStorage.setItem(STRING_ACCESS_TOKEN_TUTTIENGLISH, response.token);
        localStorage.setItem(STRING_SIDEBAR_EXPANDED_TUTTIENGLISH, true);
        history.push("/dashboard");
      } else {
        alert(t("alert_usuario_o_contrasena_incorrecto"));
      }
    } catch (err) {
      console.log("submit err");
      console.log(err);
      setLoading(false);
    }
  };

  const LanguageDropdown = () => (
    <div className="relative language-dropdown">
      <button
        type="button"
        onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition border border-transparent hover:border-indigo-100"
        aria-expanded={isLanguageDropdownOpen}
        aria-haspopup="listbox"
      >
        <span className="text-xl" aria-hidden>
          {languages.find((lang) => lang.value === selectedLanguage)?.flag}
        </span>
        <span className="text-slate-700 text-sm font-medium">
          {languages.find((lang) => lang.value === selectedLanguage)?.label}
        </span>
        <svg
          className={`w-4 h-4 text-slate-500 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isLanguageDropdownOpen && (
        <div
          className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50"
          role="listbox"
        >
          {languages.map((language) => (
            <button
              type="button"
              key={language.value}
              onClick={() => {
                setSelectedLanguage(language.value);
                setIsLanguageDropdownOpen(false);
                i18n.changeLanguage(language.value);
                localStorage.setItem(STRING_LANGUAGE_TUTTIENGLISH, language.value);
              }}
              className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-indigo-50 transition text-left ${selectedLanguage === language.value ? "bg-indigo-50 text-indigo-800" : "text-slate-700"}`}
              role="option"
              aria-selected={selectedLanguage === language.value}
            >
              <span className="text-xl" aria-hidden>
                {language.flag}
              </span>
              <span className="font-medium">{language.label}</span>
              {selectedLanguage === language.value && (
                <svg className="w-4 h-4 ml-auto text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased selection:bg-indigo-200 selection:text-indigo-950">
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-indigo-400/20 blur-3xl" />
        <div className="absolute top-1/3 -left-24 h-80 w-80 rounded-full bg-violet-400/15 blur-3xl" />
      </div>

      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3.5 gap-3">
            <button
              type="button"
              onClick={() => history.push("/landing")}
              className="text-left font-bold text-xl sm:text-2xl tracking-tight text-indigo-900 hover:text-indigo-700 transition shrink-0"
            >
              {t("te_brand")}
            </button>

            <div className="hidden sm:flex items-center gap-4 text-sm">
              <button
                type="button"
                onClick={() => history.push("/landing")}
                className="text-slate-600 hover:text-indigo-600 font-medium transition"
              >
                {t("label_volver_al_inicio")}
              </button>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <button
                type="button"
                className="sm:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200"
                onClick={() => setMobileNavOpen((o) => !o)}
                aria-expanded={mobileNavOpen}
                aria-label="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileNavOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
              <div className="hidden sm:block">
                <LanguageDropdown />
              </div>
            </div>
          </div>

          {mobileNavOpen && (
            <div className="sm:hidden border-t border-slate-100 py-3 flex flex-col gap-2 px-1">
              <button
                type="button"
                onClick={() => {
                  history.push("/landing");
                  setMobileNavOpen(false);
                }}
                className="text-left px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
              >
                {t("label_volver_al_inicio")}
              </button>
            </div>
          )}

          <div className="sm:hidden border-t border-slate-100 py-2 flex justify-center">
            <LanguageDropdown />
          </div>
        </div>
      </header>

      <main className="relative px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-14 sm:pb-24 flex flex-col items-center">
        <div className="w-full max-w-md">
          <span className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200/60">
            <span aria-hidden>🔐</span>
            {t("label_iniciar_sesion")}
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-3">
            {t("te_brand")}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8">
            {t("label_plataforma_de_nutricion")}
          </p>

          <div className="relative rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-9 shadow-xl shadow-slate-900/5">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/5 to-violet-500/10 pointer-events-none" />
            <form onSubmit={submit} className="relative space-y-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">
                  {t("label_iniciar_sesion")}
                </h2>
                <p className="text-sm text-slate-600">
                  {t("label_ingresa_tus_credenciales_para_continuar")}
                </p>
              </div>

              <div>
                <label htmlFor="usuario_login" className="block text-sm font-semibold text-slate-800 mb-1.5">
                  {t("label_usuario")}
                </label>
                <input
                  id="usuario_login"
                  type="text"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  placeholder={t("label_ingresa_tu_usuario")}
                  required
                  disabled={loading}
                  autoComplete="username"
                />
              </div>

              <div>
                <label htmlFor="password_login" className="block text-sm font-semibold text-slate-800 mb-1.5">
                  {t("label_contrasena")}
                </label>
                <div className="relative">
                  <input
                    id="password_login"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition text-base disabled:opacity-60 disabled:cursor-not-allowed"
                    placeholder={t("label_ingresa_tu_contrasena")}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-indigo-600 focus:outline-none transition-colors"
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <div className="relative w-5 h-5">
                      <Eye
                        className={`absolute inset-0 transition-all duration-300 ${showPassword ? "opacity-0 scale-75 rotate-90" : "opacity-100 scale-100 rotate-0"}`}
                        size={20}
                      />
                      <EyeOff
                        className={`absolute inset-0 transition-all duration-300 ${showPassword ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-75 -rotate-90"}`}
                        size={20}
                      />
                    </div>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center items-center gap-2 bg-indigo-600 text-white px-8 py-3.5 rounded-xl text-base font-semibold hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t("label_cargando")}
                  </>
                ) : (
                  t("label_ingresar")
                )}
              </button>

              <div className="pt-4 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-500">
                  version {VERSION_TUTTIENGLISH}
                </p>
              </div>
            </form>
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => history.push("/landing")}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-indigo-600 font-medium transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t("label_volver_al_inicio")}
            </button>
          </div>
        </div>
      </main>

      <footer className="relative bg-slate-950 text-slate-300 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-white font-bold text-lg">{t("te_brand")}</p>
          <p className="text-sm text-slate-400 mt-2">{t("te_footer_line")}</p>
          <p className="text-sm text-slate-500 mt-6">
            © {currentYear} {t("te_brand")}. {t("te_footer_rights")}
          </p>
        </div>
      </footer>
    </div>
  );
}
